import type { Player as ShakaPlayer } from 'shaka-player/dist/shaka-player.dash.js'
import { Ref, onBeforeUnmount, ref } from 'vue'

import { CLOUDFLARE_CUSTOMER_URL } from '@/constants/general'
import { timer } from '@/utils/helpers'

export const useVideoController = (
    options: Ref<{ videoId?: string; videoUrl?: string }>,
    videoElem: Ref<HTMLVideoElement | undefined>,
    isVideoVisible: Ref<boolean>,
) => {
    const shakaPlayerRef = ref<ShakaPlayer>()
    async function showVideo() {
        const { videoId, videoUrl } = options.value

        // if no video URL or ID is provided, do not show the video
        if (!videoUrl && !videoId) {
            return
        }

        // mount empty video element
        isVideoVisible.value = true

        // wait to prevent loading videos on every accidental mouse move over the card
        await timer(100)

        // sync playing state with data-attribute to control css animation
        videoElem.value?.addEventListener(
            'playing',
            () => {
                if (videoElem.value) {
                    videoElem.value.dataset.playing = 'true'
                }
            },
            { once: true },
        )

        if (videoUrl) {
            playVideoByURL(videoUrl)
        } else if (videoId) {
            playVideoByID(videoId)
        }
    }

    async function closeVideo() {
        try {
            await shakaPlayerRef.value?.destroy()
        } catch {
            // ignore destroy errors
        }
        isVideoVisible.value = false
    }

    function playVideoByURL(videoUrl: string) {
        if (!videoElem.value) {
            return
        }
        videoElem.value.src = videoUrl
    }

    async function playVideoByID(videoId: string) {
        let videoElemRef = videoElem.value
        if (!videoElemRef) {
            return
        }

        const streamUrlPrefix = `${CLOUDFLARE_CUSTOMER_URL}${videoId}/manifest`

        // check if the browser supports HLS natively
        if (videoElemRef.canPlayType('application/vnd.apple.mpegurl') !== '') {
            videoElemRef.src = `${streamUrlPrefix}/video.m3u8`
            return
        }

        // if not, lazily load Shaka Player for DASH support
        const shaka = await import('shaka-player/dist/shaka-player.dash.js')

        // check for videoElem again, as it might be already unmounted
        videoElemRef = videoElem.value
        if (!videoElemRef) {
            return
        }

        shakaPlayerRef.value = new shaka.Player()
        shakaPlayerRef.value.configure({
            abr: {
                restrictToElementSize: true,
            },
            manifest: {
                disableAudio: true,
            },
            streaming: {
                bufferingGoal: 3,
            },
        })
        shakaPlayerRef.value.attach(videoElemRef)
        try {
            await shakaPlayerRef.value.load(`${streamUrlPrefix}/video.mpd`, 0, 'application/dash+xml')
        } catch {
            // ignore loading errors
            // it can be error 7000: load() was interrupted by destroy()
        }
    }

    onBeforeUnmount(() => {
        closeVideo()
    })

    return {
        showVideo,
        closeVideo,
    }
}
